import { useEffect, useRef, useState } from "react";
import maplibregl, { type Map as MlMap, type StyleSpecification } from "maplibre-gl";
import { circleRing, type LatLng } from "../lib/geo";
import { COUNTRY_GEOJSON_URL, type BBox } from "../lib/types";

/**
 * Esri World Imagery – global satellite/aerial basemap, no API key, no
 * labels of any kind, which is exactly what the pinpoint puzzle needs:
 * the player gets terrain, towns and rivers but nothing that spells out
 * where they are.
 */
const SATELLITE_TILES =
  "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}";
const SATELLITE_ATTRIBUTION =
  '<a href="https://www.esri.com/" target="_blank" rel="noopener noreferrer">Esri</a>, Maxar, Earthstar Geographics';

const SATELLITE_SOURCE = "satellite";
const COUNTRY_SOURCE = "country";
const COUNTRY_LAYER = "country-outline";
const TARGET_SOURCE = "target";
const TARGET_FILL_LAYER = "target-fill";
const TARGET_LINE_LAYER = "target-line";

const STYLE: StyleSpecification = {
  version: 8,
  sources: {
    [SATELLITE_SOURCE]: {
      type: "raster",
      tiles: [SATELLITE_TILES],
      tileSize: 256,
      maxzoom: 19,
      attribution: SATELLITE_ATTRIBUTION,
    },
  },
  layers: [{ id: SATELLITE_SOURCE, type: "raster", source: SATELLITE_SOURCE }],
};

/** Bounding box of Czechia (approx) – the initial camera frame. */
const CZ_BOUNDS: BBox = [12.09, 48.55, 18.86, 51.06];
/**
 * Hard pan limit. Padded well beyond the border so the country never
 * gets pinned against the edge of the viewport, but tight enough that
 * the player can't wander off to a landmark that gives the game away.
 */
const CZ_MAX_BOUNDS: BBox = [10.6, 47.6, 20.4, 52.0];

const MIN_ZOOM = 6;
/**
 * The imagery has no labels, so zooming in is never cheating – it's how
 * you read a town's shape. Capped only to keep tile traffic sane.
 */
const MAX_ZOOM = 14;

const TARGET_FILL = "#22c55e";
const TARGET_OUTLINE = "#166534";
const COUNTRY_OUTLINE = "#fbbf24";

export interface PinpointMapProps {
  /** Pin the player has dropped but not confirmed yet. */
  pending: LatLng | null;
  onPick: (point: LatLng) => void;
  /** Confirmed pins that missed, oldest first. */
  misses: LatLng[];
  /** The winning pin, once the tier is solved. */
  hit: LatLng | null;
  /** Target + its acceptance radius, revealed only when the tier is over. */
  reveal: { point: LatLng; label: string; radiusKm: number } | null;
  /** Czechia outline hint. */
  outline: boolean;
  /** No more pins accepted (tier solved, or the day is over). */
  locked: boolean;
  /** Changing this re-frames the camera on the whole country. */
  frameKey: string;
}

export function PinpointMap({
  pending,
  onPick,
  misses,
  hit,
  reveal,
  outline,
  locked,
  frameKey,
}: PinpointMapProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<MlMap | null>(null);
  const markersRef = useRef<maplibregl.Marker[]>([]);
  const [loaded, setLoaded] = useState(false);
  const lockedRef = useRef(locked);
  lockedRef.current = locked;
  const onPickRef = useRef(onPick);
  onPickRef.current = onPick;
  // The reveal fly-in should happen once per tier, not on every render
  // that re-supplies the same target.
  const lastRevealKeyRef = useRef<string | null>(null);

  // ------------------------------------------------------------------
  //  Initialise the map exactly once
  // ------------------------------------------------------------------
  useEffect(() => {
    if (!containerRef.current) return;
    const map = new maplibregl.Map({
      container: containerRef.current,
      style: STYLE,
      bounds: CZ_BOUNDS,
      fitBoundsOptions: { padding: 12 },
      maxBounds: [
        [CZ_MAX_BOUNDS[0], CZ_MAX_BOUNDS[1]],
        [CZ_MAX_BOUNDS[2], CZ_MAX_BOUNDS[3]],
      ],
      minZoom: MIN_ZOOM,
      maxZoom: MAX_ZOOM,
      attributionControl: { compact: true },
      renderWorldCopies: false,
      dragRotate: false,
      pitchWithRotate: false,
      touchPitch: false,
    });
    map.touchZoomRotate.disableRotation();
    map.addControl(new maplibregl.NavigationControl({ showCompass: false }), "top-right");
    mapRef.current = map;

    map.on("click", (e) => {
      if (lockedRef.current) return;
      onPickRef.current({ lat: e.lngLat.lat, lng: e.lngLat.lng });
    });

    map.on("load", () => setLoaded(true));

    return () => {
      for (const m of markersRef.current) m.remove();
      markersRef.current = [];
      map.remove();
      mapRef.current = null;
      setLoaded(false);
    };
    // Intentionally one-shot.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ------------------------------------------------------------------
  //  Cursor affordance – a crosshair while the player may still guess
  // ------------------------------------------------------------------
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !loaded) return;
    map.getCanvas().style.cursor = locked ? "" : "crosshair";
  }, [locked, loaded]);

  // ------------------------------------------------------------------
  //  Markers (pending pin, past misses, the hit, the revealed target)
  //
  //  There are at most a handful, so a full teardown/rebuild is both
  //  cheaper to reason about and fast enough.
  // ------------------------------------------------------------------
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !loaded) return;
    for (const m of markersRef.current) m.remove();
    markersRef.current = [];

    const add = (point: LatLng, className: string, label?: string) => {
      const el = document.createElement("div");
      el.className = `pp-marker ${className}`;
      if (label) {
        const span = document.createElement("span");
        span.className = "pp-marker-label";
        span.textContent = label;
        el.appendChild(span);
      }
      const marker = new maplibregl.Marker({ element: el, anchor: "center" })
        .setLngLat([point.lng, point.lat])
        .addTo(map);
      markersRef.current.push(marker);
    };

    misses.forEach((miss, i) => add(miss, "pp-marker-miss", String(i + 1)));
    if (hit) add(hit, "pp-marker-hit");
    if (reveal) add(reveal.point, "pp-marker-target", reveal.label);
    if (pending) add(pending, "pp-marker-pending");
  }, [pending, misses, hit, reveal, loaded]);

  // ------------------------------------------------------------------
  //  Re-frame on the whole country when the player switches tier/day.
  //
  //  Declared before the reveal effect so that when a switch lands on
  //  an already-solved tier, the fly-to-the-answer animation is the one
  //  that wins.
  // ------------------------------------------------------------------
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !loaded) return;
    map.fitBounds(
      [
        [CZ_BOUNDS[0], CZ_BOUNDS[1]],
        [CZ_BOUNDS[2], CZ_BOUNDS[3]],
      ],
      { padding: 12, duration: 600, essential: true },
    );
    // Only on an explicit frame change – panning shouldn't be undone.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [frameKey, loaded]);

  // ------------------------------------------------------------------
  //  Revealed target: the acceptance circle + a fly-in so the player
  //  immediately sees how close they were.
  // ------------------------------------------------------------------
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !loaded) return;

    const data: GeoJSON.FeatureCollection = {
      type: "FeatureCollection",
      features: reveal
        ? [
            {
              type: "Feature",
              properties: {},
              geometry: {
                type: "Polygon",
                coordinates: [circleRing(reveal.point, reveal.radiusKm)],
              },
            },
          ]
        : [],
    };

    const source = map.getSource(TARGET_SOURCE) as maplibregl.GeoJSONSource | undefined;
    if (source) {
      source.setData(data);
    } else {
      map.addSource(TARGET_SOURCE, { type: "geojson", data });
      map.addLayer({
        id: TARGET_FILL_LAYER,
        type: "fill",
        source: TARGET_SOURCE,
        paint: { "fill-color": TARGET_FILL, "fill-opacity": 0.18 },
      });
      map.addLayer({
        id: TARGET_LINE_LAYER,
        type: "line",
        source: TARGET_SOURCE,
        paint: { "line-color": TARGET_OUTLINE, "line-width": 2 },
      });
    }

    if (!reveal) {
      lastRevealKeyRef.current = null;
      return;
    }
    const key = `${reveal.label}:${reveal.point.lat},${reveal.point.lng}`;
    if (lastRevealKeyRef.current === key) return;
    lastRevealKeyRef.current = key;
    map.flyTo({
      center: [reveal.point.lng, reveal.point.lat],
      zoom: Math.max(map.getZoom(), 9.5),
      duration: 900,
      essential: true,
    });
  }, [reveal, loaded]);

  // ------------------------------------------------------------------
  //  Czechia outline hint
  // ------------------------------------------------------------------
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !loaded || !outline) return;
    if (map.getLayer(COUNTRY_LAYER)) {
      map.setLayoutProperty(COUNTRY_LAYER, "visibility", "visible");
      return;
    }
    let cancelled = false;
    fetch(COUNTRY_GEOJSON_URL)
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then((data: GeoJSON.GeoJSON) => {
        const live = mapRef.current;
        if (cancelled || !live || live.getLayer(COUNTRY_LAYER)) return;
        live.addSource(COUNTRY_SOURCE, { type: "geojson", data });
        live.addLayer(
          {
            id: COUNTRY_LAYER,
            type: "line",
            source: COUNTRY_SOURCE,
            paint: {
              "line-color": COUNTRY_OUTLINE,
              "line-width": 2,
              "line-opacity": 0.9,
            },
          },
          // Under the target circle so a revealed answer stays legible.
          live.getLayer(TARGET_FILL_LAYER) ? TARGET_FILL_LAYER : undefined,
        );
      })
      .catch((err: unknown) => {
        console.error(`Failed to load ${COUNTRY_GEOJSON_URL}`, err);
      });
    return () => {
      cancelled = true;
    };
  }, [outline, loaded]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !loaded || outline || !map.getLayer(COUNTRY_LAYER)) return;
    map.setLayoutProperty(COUNTRY_LAYER, "visibility", "none");
  }, [outline, loaded]);

  return (
    <div className="pinpoint-map-wrap">
      <div ref={containerRef} className="pinpoint-map" />
      {!loaded && <div className="map-loading">Načítám mapu…</div>}
    </div>
  );
}
